import {
  CAMERA_STORAGE_KEY,
  CHROME_TOP_PHONE_PX,
  CHROME_TOP_PX,
  CREDIT_PHONE_PX,
  DETAIL_PANEL_RESERVE_PX,
  DETAIL_SHEET_HEIGHT,
  FLIGHT_MAX_MS,
  FLIGHT_MIN_MS,
  FLIGHT_STEP_MS,
  GLOBAL_BBOX_LNG_SPAN,
  INITIAL_CENTER,
  INITIAL_ZOOM,
  MAX_ZOOM,
  MIN_FIT_PX,
  PHONE_MAX_WIDTH_PX,
  PHONE_MEDIA_QUERY,
  PLACE_FIT_PADDING,
  PLACE_FIT_PADDING_PHONE,
  PLACE_FLY_ZOOM,
  SAME_CAMERA_PX,
  SAME_CAMERA_ZOOM,
  VISIBLE_EDGE_PX,
} from './map_config'
import type { MapViewport } from '../types/atlas'

// Where the camera goes, worked out WITHOUT a map.
//
// Everything here is arithmetic on numbers the caller already has, and that is
// the point of the file. The map has to be CONSTRUCTED at its destination — a
// deep link that builds at the world view and flies in spends 3.5 to 4.2 s and
// about 300 tile requests on ground nobody looks at — and at construction there
// is no map to ask where a point would land. It is also why the layout can
// import this statically: nothing here touches maplibre-gl, so nothing here
// pulls the map chunk into the entry bundle.

/**
 * What the camera is asked to show.
 *
 * Taken from props rather than from rendered geometry, so it works before the
 * polygon is drawn — and before the region layer has even been fetched.
 */
export interface PlaceCamera {
  /**
   * WHAT the camera is for, as a key: the hook moves when this changes and not
   * otherwise, so panning away from a place and back to the same panel does not
   * yank the camera. A place's is its slug. A winery's is prefixed by the
   * layout, because a producer and an appellation can share a slug.
   */
  slug: string
  bbox: [number, number, number, number] | null
  point: [number, number] | null
  /**
   * What to fly to `point` at. Absent for a place's own point, where
   * `PLACE_FLY_ZOOM` is right. Present when the point belongs to an ANCESTOR —
   * a Chilean DO with no shape of its own locates to Chile, and flying there at
   * appellation zoom would show a reader one vineyard's worth of ground and
   * imply the appellation is on it — and for a winery.
   */
  zoom?: number
  /**
   * Set for a winery, and it makes the move CONDITIONAL: the camera stays where
   * it is when the point is already on show.
   *
   * "On show" is two things. The point is in the part of the map the chrome is
   * not covering, and the camera is at least this deep — the zoom from which a
   * winery is drawn as a dot of its own rather than counted into a cluster
   * (`map_zoom.winery_points`). A reader who clicks a dot they can see keeps
   * the view they chose; one who picks a producer from the search box while
   * looking at the whole of California is taken to it, because a panel beside
   * a cluster chip reading "311" describes nothing on the map.
   */
  visibleFrom?: number
}

/** How much of each edge of the canvas the chrome is covering, in pixels. */
export interface Insets {
  top: number
  right: number
  bottom: number
  left: number
}

export interface Size {
  width: number
  height: number
}

export interface CameraPosition {
  center: [number, number]
  zoom: number
}

export const NO_INSETS: Insets = { top: 0, right: 0, bottom: 0, left: 0 }

/**
 * What the chrome covers right now. See the constants in `map_config.ts` for
 * where each number comes from.
 *
 * Read at the moment the camera moves rather than held in state: it depends on
 * the window as well as on the panel, and a window resized since the last
 * render has not told React about it.
 *
 * `dockHeight` is the measured height of everything anchored to the bottom
 * edge on a phone — the sheet and the attribution on top of it. Without one
 * (nothing rendered yet, or a test, where nothing is laid out) the bottom is
 * taken to be the most it can be.
 */
export function chromeInsets(panelOpen: boolean, dockHeight?: number): Insets {
  const phone = window.matchMedia?.(PHONE_MEDIA_QUERY).matches ?? false

  if (phone) {
    const most = (panelOpen ? Math.round(window.innerHeight * DETAIL_SHEET_HEIGHT) : 0) + CREDIT_PHONE_PX
    const bottom = dockHeight !== undefined && dockHeight > 0 ? Math.round(dockHeight) : most

    return { top: CHROME_TOP_PHONE_PX, right: 0, bottom, left: 0 }
  }

  return { top: CHROME_TOP_PX, right: panelOpen ? DETAIL_PANEL_RESERVE_PX : 0, bottom: 0, left: 0 }
}

// One axis of `fitPadding`: the chrome at each end plus a margin, cut back until
// at least `MIN_FIT_PX` is left between them. The margin goes first — it is
// breathing room — and only then is the chrome's own share scaled, which lets
// the place run a little under it rather than not fitting at all.
function padAxis(before: number, after: number, extent: number, margin: number): [number, number] {
  const room = Math.max(0, extent - MIN_FIT_PX)
  const spare = Math.max(0, room - before - after)
  const each = Math.min(margin, spare / 2)
  const covered = before + after
  const scale = covered > room && covered > 0 ? room / covered : 1

  return [before * scale + each, after * scale + each]
}

/**
 * The `padding` a `fitBounds` takes: the chrome, and a margin inside it.
 *
 * The margin is chosen by how wide the UNCOVERED part is, not the window. A
 * tablet with the panel open has 360px of map beside it — a phone's worth —
 * and the desktop margin on both sides of that left Temecula 232px to be
 * drawn in.
 *
 * It always leaves room. MapLibre answers a padding larger than the canvas by
 * warning and not moving — and from the constructor that is a map left at the
 * world view with a panel describing somewhere else.
 */
export function fitPadding(insets: Insets, size: Size): Insets {
  const uncovered = size.width - insets.left - insets.right
  const margin = uncovered <= PHONE_MAX_WIDTH_PX ? PLACE_FIT_PADDING_PHONE : PLACE_FIT_PADDING
  const [left, right] = padAxis(insets.left, insets.right, size.width, margin)
  const [top, bottom] = padAxis(insets.top, insets.bottom, size.height, margin)

  return { top: Math.round(top), right: Math.round(right), bottom: Math.round(bottom), left: Math.round(left) }
}

/**
 * Where the middle of the uncovered part is, measured from the middle of the
 * canvas. It is the `offset` a `flyTo` or an `easeTo` takes to land a point
 * there.
 */
export function visibleOffset(insets: Insets): [number, number] {
  return [(insets.left - insets.right) / 2, (insets.top - insets.bottom) / 2]
}

// Web Mercator, as MapLibre lays it out: the world is one 512px square at zoom
// 0 and doubles with every level. `x` and `y` are fractions of that square.
const WORLD_PX = 512
const MAX_MERCATOR_LAT = 85.051129

const worldSize = (zoom: number) => WORLD_PX * 2 ** zoom
const mercatorX = (lng: number) => (lng + 180) / 360
const mercatorY = (lat: number) => {
  const clamped = Math.min(Math.max(lat, -MAX_MERCATOR_LAT), MAX_MERCATOR_LAT)

  return (1 - Math.log(Math.tan(Math.PI / 4 + (clamped * Math.PI) / 360)) / Math.PI) / 2
}
const lngOf = (x: number) => x * 360 - 180
const latOf = (y: number) => (360 / Math.PI) * Math.atan(Math.exp((1 - 2 * y) * Math.PI)) - 90

// How far `point` is drawn from the middle of the canvas, in pixels, for a
// camera with no bearing or pitch. The short way round the antimeridian: Fiji
// is 20px east of a camera at 179.9, not 37,000px west of it.
function screenOffset(camera: CameraPosition, point: [number, number]): [number, number] {
  const size = worldSize(camera.zoom)
  let dx = mercatorX(point[0]) - mercatorX(camera.center[0])
  dx -= Math.round(dx)

  return [dx * size, (mercatorY(point[1]) - mercatorY(camera.center[1])) * size]
}

/** The camera centre that draws `point` at `offset` from the middle of the canvas. */
export function centreFor(point: [number, number], zoom: number, offset: [number, number]): [number, number] {
  const size = worldSize(zoom)

  return [
    lngOf(mercatorX(point[0]) - offset[0] / size),
    latOf(mercatorY(point[1]) - offset[1] / size),
  ]
}

/**
 * The ground a camera with no bearing or pitch shows on a canvas of `size`, in
 * the shape the map reports a settled frame in.
 *
 * It is how a move can say where it will LAND before it has left. A flight to
 * Santa Ynez takes about a second, and the appellations around Santa Ynez used
 * to be asked for only once it was over; with the landing frame known at
 * takeoff they are asked for then, and are drawn by the time the camera gets
 * there.
 *
 * Clamped to the world like every frame the map reports. A frame that would
 * cross the antimeridian is cut at it rather than widened to the whole world:
 * this is a guess made to save a round trip, and half of it right is a smaller
 * request than all of it wrong.
 */
export function frameAt(camera: CameraPosition, size: Size): MapViewport {
  const world = worldSize(camera.zoom)
  const x = mercatorX(camera.center[0])
  const y = mercatorY(camera.center[1])
  const halfWidth = size.width / world / 2
  const halfHeight = size.height / world / 2

  return {
    west: Math.max(lngOf(x - halfWidth), -180),
    south: Math.max(latOf(Math.min(y + halfHeight, 1)), -90),
    east: Math.min(lngOf(x + halfWidth), 180),
    north: Math.min(latOf(Math.max(y - halfHeight, 0)), 90),
    zoom: camera.zoom,
  }
}

/** Whether a canvas position is in the part the chrome leaves uncovered. */
export function inVisiblePart(at: { x: number; y: number }, size: Size, insets: Insets): boolean {
  return (
    at.x >= insets.left + VISIBLE_EDGE_PX &&
    at.x <= size.width - insets.right - VISIBLE_EDGE_PX &&
    at.y >= insets.top + VISIBLE_EDGE_PX &&
    at.y <= size.height - insets.bottom - VISIBLE_EDGE_PX
  )
}

/** The same question for a camera that is not a map yet. */
export function pointOnShow(camera: CameraPosition, size: Size, insets: Insets, point: [number, number]): boolean {
  const [dx, dy] = screenOffset(camera, point)

  return inVisiblePart({ x: size.width / 2 + dx, y: size.height / 2 + dy }, size, insets)
}

/**
 * How long a move from one camera to another takes (`FLIGHT_MIN_MS` has the
 * reasoning): the minimum, plus a step for each zoom level crossed and for
 * each doubling of the distance.
 *
 * The distance is counted in SCREENS, at the wider of the two zooms — what a
 * reader would have to scroll, from the more zoomed-out end of the trip. In
 * degrees it would mean nothing: Temecula to Santa Ynez is three degrees and
 * six screens, and three degrees at the world view is the width of a thumb.
 * Doublings rather than a straight count because a flight zooms out to cover
 * ground, so sixty screens are not ten times the trip that six are.
 */
export function flightMs(from: CameraPosition, to: CameraPosition, size: Size): number {
  const zoom = Math.min(from.zoom, to.zoom)
  const [dx, dy] = screenOffset({ center: from.center, zoom }, to.center)
  const screens = Math.hypot(dx, dy) / Math.max(size.width, size.height, 1)
  const steps = Math.abs(to.zoom - from.zoom) + Math.log2(1 + screens)

  return Math.round(Math.min(FLIGHT_MAX_MS, FLIGHT_MIN_MS + FLIGHT_STEP_MS * steps))
}

/**
 * Whether a move from one camera to the other would travel nowhere
 * (`SAME_CAMERA_PX` has the reasoning and the tolerance): the zooms a
 * hundredth of a level apart at most, and the centres a pixel apart at most on
 * the canvas of the camera the map has.
 *
 * `flightMs` cannot answer this. It is the minimum for a move that goes
 * nowhere and for a nudge alike, and only one of the two is skipped.
 *
 * Like everything in this file it knows no bearing and no pitch: two cameras
 * with one centre and one zoom are the same camera only on a map that is
 * north-up and flat, and the caller is the one who can ask the map that.
 */
export function sameCamera(from: CameraPosition, to: CameraPosition): boolean {
  // Each asked as "is it within", so a number that is not one answers no and
  // the move is made.
  if (!(Math.abs(to.zoom - from.zoom) <= SAME_CAMERA_ZOOM)) return false

  return Math.hypot(...screenOffset(from, to.center)) <= SAME_CAMERA_PX
}

/**
 * The box to fit, or null when the camera goes to a point instead.
 *
 * An antimeridian-straddling bbox reports the whole world, so fitting it would
 * zoom out to the globe. The representative point is derived from the largest
 * part and is correct, so that is what the camera uses when there is one.
 */
export function boundsToFit(camera: PlaceCamera): [number, number, number, number] | null {
  if (!camera.bbox) return null
  if (camera.bbox[2] - camera.bbox[0] >= GLOBAL_BBOX_LNG_SPAN && camera.point) return null

  return camera.bbox
}

const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value)

// Storage can throw — a browser with site data blocked raises on the property
// access itself — and a camera that cannot be remembered is not worth an error
// on a map. Both directions are quiet for that reason.
export function rememberCamera(position: CameraPosition): void {
  try {
    const [lng, lat] = position.center
    // Five decimals is about a metre; the string is written on every settle.
    const stored = [Number(lng.toFixed(5)), Number(lat.toFixed(5)), Number(position.zoom.toFixed(2))]

    window.sessionStorage.setItem(CAMERA_STORAGE_KEY, JSON.stringify(stored))
  } catch {
    // Nothing to do: the next map opens at the world view, as it always did.
  }
}

/**
 * The last settled camera, or null. Checked rather than trusted: it is a string
 * anything on the origin could have written, and it goes straight into the Map
 * constructor, which throws on a latitude of 91.
 */
export function rememberedCamera(): CameraPosition | null {
  try {
    const stored: unknown = JSON.parse(window.sessionStorage.getItem(CAMERA_STORAGE_KEY) ?? 'null')
    if (!Array.isArray(stored) || stored.length !== 3) return null

    const [lng, lat, zoom] = stored as unknown[]
    if (!finite(lng) || !finite(lat) || !finite(zoom)) return null
    if (Math.abs(lng) > 180 || Math.abs(lat) > 90 || zoom < 0 || zoom > MAX_ZOOM) return null

    return { center: [lng, lat], zoom }
  } catch {
    return null
  }
}

/** What the Map constructor is given. */
export interface OpeningCamera extends CameraPosition {
  bounds?: [number, number, number, number]
  fitBoundsOptions?: { padding: Insets }
  /**
   * The `PlaceCamera.slug` this view already shows, so the hook does not then
   * fly to where it is standing. Null when the map opened somewhere of its own.
   */
  slug: string | null
}

/**
 * Where a map that is about to be constructed should open.
 *
 * - On a place with a shape: fitted to it. `center` and `zoom` are still given,
 *   because MapLibre jumps to them first and then applies `bounds`.
 * - On a place with only a point, or a winery: at the point, drawn in the
 *   middle of what the chrome leaves uncovered.
 * - On a winery the remembered camera was already showing: at the remembered
 *   camera. A reload of a producer's page keeps the reader's view for the same
 *   reason a click on its dot does.
 * - Otherwise: wherever the reader last was in this tab, or the world.
 */
export function openingCamera(camera: PlaceCamera | null, insets: Insets, size: Size): OpeningCamera {
  const remembered = rememberedCamera()
  const resting: CameraPosition = remembered ?? { center: INITIAL_CENTER, zoom: INITIAL_ZOOM }
  if (!camera) return { ...resting, slug: null }

  const bounds = boundsToFit(camera)
  if (bounds) return { ...resting, bounds, fitBoundsOptions: { padding: fitPadding(insets, size) }, slug: camera.slug }

  // A place with neither a shape nor a point opens its panel over whatever the
  // map was showing. The slug is still recorded: there is nowhere to fly to.
  if (!camera.point) return { ...resting, slug: camera.slug }

  if (
    camera.visibleFrom !== undefined &&
    remembered &&
    remembered.zoom >= camera.visibleFrom &&
    pointOnShow(remembered, size, insets, camera.point)
  ) {
    return { ...remembered, slug: camera.slug }
  }

  const zoom = camera.zoom ?? PLACE_FLY_ZOOM

  return { center: centreFor(camera.point, zoom, visibleOffset(insets)), zoom, slug: camera.slug }
}
